import { useEffect, useState } from "react";
import { NoFailedResource } from "storefront/components/Modals/GetGrailedLabelAction";
import {
  getGpfsEligibilityForUser,
  GpfsEligibility,
} from "storefront/GrailedAPI/v1/Users/GrailedPaysForShipping";
import useAuthentication from "storefront/hooks/useAuthentication";
import { completed, loading } from "storefront/lib/Resource";

/**
 * Custom React hook to fetch and manage GPFS (Grailed Pays for Shipping) eligibility for a given user.
 *
 * @param userId - The unique identifier of the user whose GPFS eligibility is to be checked. If not provided, the hook will immediately mark the fetch as complete with a null eligibility.
 * @returns A `NoFailedResource<GpfsEligibility>` object representing the loading or completed state of the GPFS eligibility fetch operation.
 *
 * The hook:
 * - Initiates a fetch for GPFS eligibility when a valid `userId` is provided.
 * - Sets the eligibility state upon successful fetch.
 * - Handles loading and completed states for consumers.
 */
export default function useGpfs(): NoFailedResource<GpfsEligibility> {
  const [gpfsEligibility, setGpfsEligibility] = useState<GpfsEligibility>(null);
  const [fetched, setFetched] = useState(false);
  const auth = useAuthentication();

  useEffect(() => {
    // everything hinges on authentication, so wait for it before proceeding
    if (auth.type === "Loading") return;

    if (auth.type !== "Authenticated") {
      setFetched(true);
      return;
    }

    const fetchGpfsEligibility = async () => {
      try {
        const gpfsValue = await getGpfsEligibilityForUser(auth.user.id);
        setGpfsEligibility(gpfsValue);
      } finally {
        setFetched(true);
      }
    };

    fetchGpfsEligibility();
  }, [auth]);

  return fetched ? completed(gpfsEligibility) : loading;
}
